import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { styled } from '@mui/material';

import { CRM_FOLLOW_UPS_DUE } from './crmShell.queries';

export const SidebarCountPill = styled('span')`
  margin-left: 0.5rem;
  min-width: 1.5rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.25rem;
  text-align: center;
  color: #111827;
  background-color: #fbbf24;
`;

/** Above this the pill reads "999+": the exact figure is on the screen itself. */
const MAX_SHOWN = 999;

export const formatDueCount = (count: number) =>
  count > MAX_SHOWN ? `${MAX_SHOWN}+` : count.toLocaleString('en-US');

/**
 * The moment this page load first asked, shared by every mount. The badge moves
 * between the collapsed Outreach heading and its Follow-ups row; with the same
 * variables each new mount reads Apollo's cache instead of asking again.
 */
let firstAskedAt: string | undefined;
const pageLoadMoment = () => {
  if (firstAskedAt === undefined) firstAskedAt = new Date().toISOString();
  return firstAskedAt;
};

interface FollowUpsDueBadgeProps {
  /** For tests. "Now" is fixed once per mount so the query variables never change under it. */
  now?: () => Date;
}

/**
 * How many follow-ups are due: beside the sidebar's Follow-ups entry, or on the
 * Outreach heading while that section is collapsed.
 *
 * Read once per page load and never on a timer: it shares the admin's
 * default-query budget with every screen. Shows nothing while loading, at
 * zero, or on any error — an API without card 16 answers this query with a
 * validation error, and a failed count must not look like "none due". A guard
 * denial still signs out through the global error link.
 */
export const FollowUpsDueBadge = ({ now }: FollowUpsDueBadgeProps) => {
  const [due] = useState(() => (now ? now().toISOString() : pageLoadMoment()));

  const { data } = useQuery(CRM_FOLLOW_UPS_DUE, { variables: { due } });

  const total: number | undefined = data?.crmCalls?.total;

  if (!total) return null;

  return (
    <SidebarCountPill
      aria-label={`${total.toLocaleString('en-US')} follow-up${total === 1 ? '' : 's'} due`}
      title={`${total.toLocaleString('en-US')} due`}
    >
      {formatDueCount(total)}
    </SidebarCountPill>
  );
};
